import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { useAdAccount } from "../lib/adAccount";
import {
  AreaChart, Area, XAxis, YAxis, ResponsiveContainer, Tooltip, CartesianGrid
} from "recharts";
import {
  TrendUp, CurrencyCircleDollar, CursorClick, UserPlus, MapPin, Coins, ChartLineUp
} from "@phosphor-icons/react";
import DateRangePicker from "../components/DateRangePicker";
import BillingBanner from "../components/BillingBanner";
import RefreshButton from "../components/RefreshButton";
import LiveSyncButton from "../components/LiveSyncButton";

const fmtMoney = (n) => "RM" + (n || 0).toLocaleString("en-MY", { maximumFractionDigits: 2 });
const fmtNum = (n) => (n || 0).toLocaleString("en-MY");

function StatCard({ label, value, sub, icon: Icon, accent }) {
  return (
    <div data-testid={`stat-${label}`} className="bg-zinc-900/40 border border-zinc-800/60 p-4">
      <div className="flex items-center justify-between mb-3">
        <span className="text-[10px] font-mono tracking-[0.2em] uppercase text-zinc-500">{label}</span>
        <Icon size={14} className={accent || "text-zinc-500"} />
      </div>
      <div className="font-display text-2xl font-bold tracking-tight">{value}</div>
      {sub && <div className="text-[11px] font-mono text-zinc-500 mt-1">{sub}</div>}
    </div>
  );
}

function ChartTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-[#0A0A0A] border border-zinc-800 px-3 py-2 text-xs font-mono">
      <div className="text-zinc-500 mb-1">{label}</div>
      {payload.map((p) => (
        <div key={p.dataKey} className="text-zinc-200">
          <span className="text-zinc-500">{p.dataKey}: </span>
          <span style={{ color: p.color }}>{typeof p.value === "number" ? p.value.toLocaleString() : p.value}</span>
        </div>
      ))}
    </div>
  );
}

export default function Dashboard() {
  const { selected, accounts } = useAdAccount();
  const [data, setData] = useState(null);
  const [range, setRange] = useState({ mode: "preset", days: 7 });
  const [loading, setLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState(null);

  const load = () => {
    if (!selected) return;
    setLoading(true);
    const params = new URLSearchParams({ ad_account_id: selected });
    if (range.mode === "preset") params.set("days", range.days);
    else { params.set("date_start", range.date_start); params.set("date_stop", range.date_stop); }
    return api.get(`/dashboard/summary?${params}`)
      .then(r => { setData(r.data); setLastUpdated(Date.now()); })
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, [selected, range]); // eslint-disable-line

  const acct = accounts.find(a => a.id === selected);
  const s = data?.summary || {};
  const rangeLabel = range.mode === "preset" ? `Last ${range.days} days` : `${range.date_start} → ${range.date_stop}`;

  return (
    <div className="flex-1 overflow-y-auto">
      <div className="h-14 border-b border-zinc-900 px-6 flex items-center backdrop-blur-xl bg-zinc-950/70 sticky top-0 z-10">
        <div>
          <div className="text-[10px] font-mono tracking-[0.2em] uppercase text-zinc-500">// Dashboard</div>
          <div className="text-sm font-medium">{acct?.name || "—"} · {rangeLabel}</div>
        </div>
        <div className="ml-auto flex items-center gap-2">
          <LiveSyncButton onSynced={load} />
          <RefreshButton lastUpdated={lastUpdated} loading={loading} onRefresh={load} />
          <DateRangePicker value={range} onChange={setRange} />
        </div>
      </div>

      <div className="p-6 space-y-6">
        <BillingBanner />

        {loading && <div className="text-zinc-500 text-sm">Loading...</div>}

        {data && (
          <>
            {/* Primary KPIs — Subscribe-focused */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <StatCard label="Spend" value={fmtMoney(s.spend)} sub={rangeLabel} icon={CurrencyCircleDollar} accent="text-orange-500" />
              <StatCard label="CTR" value={(s.ctr || 0) + "%"} sub={`${fmtNum(s.clicks)} clicks`} icon={CursorClick} accent={s.ctr >= 1.5 ? "text-emerald-400" : "text-zinc-500"} />
              <StatCard label="Landing Page Views" value={fmtNum(s.landing_page_views)} sub={`${s.lp_view_rate || 0}% of clicks`} icon={MapPin} />
              <StatCard label="Subscribes" value={fmtNum(s.subscribes)} sub={`${s.subscribe_rate || 0}% of LP views`} icon={UserPlus} accent="text-emerald-400" />
            </div>

            {/* Secondary efficiency row */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <StatCard label="Cost / Subscribe" value={fmtMoney(s.cost_per_subscribe)} sub="Primary efficiency KPI" icon={ChartLineUp} accent={s.cost_per_subscribe > 0 && s.cost_per_subscribe <= 12 ? "text-emerald-400" : s.cost_per_subscribe <= 20 ? "text-amber-400" : "text-red-400"} />
              <StatCard label="CPC" value={fmtMoney(s.cpc)} sub="Cost per click" icon={Coins} />
              <StatCard label="CPM" value={fmtMoney(s.cpm)} sub="Per 1k impressions" icon={Coins} />
              <StatCard label="Impressions" value={fmtNum(s.impressions)} sub={`Reach ${fmtNum(s.reach)}`} icon={TrendUp} />
            </div>

            <div className="bg-zinc-900/40 border border-zinc-800/60 p-5">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <div className="text-[10px] font-mono tracking-[0.2em] uppercase text-zinc-500 mb-1">// Daily Spend & Conversions</div>
                  <h3 className="font-display text-lg font-semibold">Performance Trend</h3>
                </div>
              </div>
              <div className="h-64" style={{ minHeight: 256, width: "100%" }}>
                <ResponsiveContainer width="100%" height="100%" minWidth={1} minHeight={1}>
                  <AreaChart data={data.daily_series}>
                    <defs>
                      <linearGradient id="g1" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#f97316" stopOpacity={0.3} />
                        <stop offset="100%" stopColor="#f97316" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid stroke="rgba(255,255,255,0.04)" vertical={false} />
                    <XAxis dataKey="date" stroke="#52525b" fontSize={10} fontFamily="JetBrains Mono" tickFormatter={(d) => d.slice(5)} />
                    <YAxis stroke="#52525b" fontSize={10} fontFamily="JetBrains Mono" />
                    <Tooltip content={<ChartTooltip />} />
                    <Area type="monotone" dataKey="spend" stroke="#f97316" strokeWidth={2} fill="url(#g1)" />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </div>

            <div className="bg-zinc-900/40 border border-zinc-800/60">
              <div className="px-5 py-4 border-b border-zinc-800/60">
                <div className="text-[10px] font-mono tracking-[0.2em] uppercase text-zinc-500 mb-1">// Campaigns</div>
                <h3 className="font-display text-lg font-semibold">By Spend</h3>
              </div>
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-[10px] font-mono tracking-[0.15em] uppercase text-zinc-500">
                    <th className="text-left px-5 py-2.5 font-medium">Campaign</th>
                    <th className="text-right px-3 py-2.5 font-medium">Spend</th>
                    <th className="text-right px-3 py-2.5 font-medium">CTR</th>
                    <th className="text-right px-3 py-2.5 font-medium">LP Views</th>
                    <th className="text-right px-3 py-2.5 font-medium">Subscribes</th>
                    <th className="text-right px-5 py-2.5 font-medium">Cost / Sub</th>
                  </tr>
                </thead>
                <tbody>
                  {data.by_campaign.map((c) => (
                    <tr key={c.campaign_id} className="border-t border-zinc-800/40 hover:bg-zinc-900/40" data-testid={`row-${c.campaign_id}`}>
                      <td className="px-5 py-3 truncate max-w-xs">{c.campaign_name}</td>
                      <td className="px-3 py-3 text-right font-mono">{fmtMoney(c.spend)}</td>
                      <td className="px-3 py-3 text-right font-mono">{c.ctr}%</td>
                      <td className="px-3 py-3 text-right font-mono">{fmtNum(c.landing_page_views)}</td>
                      <td className="px-3 py-3 text-right font-mono font-semibold text-emerald-400">{fmtNum(c.subscribes)}</td>
                      <td className={`px-5 py-3 text-right font-mono font-semibold ${c.cost_per_subscribe > 0 && c.cost_per_subscribe <= 12 ? "text-emerald-400" : c.cost_per_subscribe <= 20 ? "text-zinc-200" : "text-red-400"}`}>
                        {fmtMoney(c.cost_per_subscribe)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
